// =============================================================================
// lib/runs.ts — external run billing (Evo1 per-run invoicing)
// =============================================================================
// Typed wrappers over the operations.fn_admin_* RPCs shipped with
// RealityDatabase migration 20260908092514_external_run_billing. Every call
// asserts booking_manage_external_runs server-side; the route gate in App.tsx
// only keeps the page out of reach for people who would get refused anyway.
import { supabase } from './supabase';
import { downscaleImage } from './image';

export type RunStatus = 'billable' | 'invoiced' | 'voided' | 'test';

export interface BillingRoom {
  room_id: string;
  room_name: string;
  customer_name: string;
  location_id: string | null;
}

export interface BillingRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  total_time_ms: number | null;
  crash_count: number;
  status: RunStatus;
  is_billable: boolean;
  billing_handled: boolean;
  billing_voided_at: string | null;
  billing_void_reason: string | null;
  voided_by_name: string | null;
  exclude_from_analytics: boolean;
  exclusion_reason: string | null;
  version: string | null;
  pc_instance_id: string | null;
  pc_name: string | null;
  experience_id: string | null;
  experience_name: string | null;
  // Ended badly (never completed / short / crashed) and the same PC
  // relaunched within 25 minutes. A hint for the reviewer, never auto-acted.
  restart_suspect: boolean;
  minutes_to_next: number | null;
  next_run_id: string | null;
  // Partner report on this run (Meldingen page), null when none was filed.
  report: RunReport | null;
}

export interface BillingRunSummary {
  total: number;
  billable: number;
  invoiced: number;
  voided: number;
  test: number;
  restart_suspects: number;
  incomplete: number;
  open_reports: number;
}

export interface BillingRunFeed {
  rows: BillingRun[];
  summary: BillingRunSummary;
}

const EMPTY_SUMMARY: BillingRunSummary = {
  total: 0, billable: 0, invoiced: 0, voided: 0, test: 0, restart_suspects: 0, incomplete: 0,
  open_reports: 0,
};

export async function listBillingRooms(): Promise<BillingRoom[]> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_admin_list_billing_rooms');
  if (error) throw error;
  return (data as BillingRoom[]) ?? [];
}

// [fromUtc, toUtc) as ISO instants; the caller decides the venue-local window.
export async function listBillingRuns(
  roomId: string,
  fromUtc: string,
  toUtc: string,
): Promise<BillingRunFeed> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_admin_list_billing_runs', {
      p_room_id: roomId,
      p_from:    fromUtc,
      p_to:      toUtc,
    });
  if (error) throw error;
  const feed = data as Partial<BillingRunFeed> | null;
  return {
    rows: feed?.rows ?? [],
    summary: { ...EMPTY_SUMMARY, ...(feed?.summary ?? {}) },
  };
}

export async function voidRun(runId: string, reason: string): Promise<void> {
  const { error } = await supabase
    .schema('operations')
    .rpc('fn_admin_void_run', { p_run_id: runId, p_reason: reason });
  if (error) throw error;
}

export async function unvoidRun(runId: string): Promise<void> {
  const { error } = await supabase
    .schema('operations')
    .rpc('fn_admin_unvoid_run', { p_run_id: runId });
  if (error) throw error;
}

// Returns how many runs were flagged.
export async function markRunsInvoiced(
  roomId: string,
  fromUtc: string,
  toUtc: string,
): Promise<number> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_admin_mark_runs_invoiced', {
      p_room_id: roomId,
      p_from:    fromUtc,
      p_to:      toUtc,
    });
  if (error) throw error;
  return (data as number | null) ?? 0;
}

// =============================================================================
// Partner reports (Evoluon) — RealityDatabase migration
// 20260923100000_booking_external_run_reports
// =============================================================================
// A reporter (booking_report_external_runs) sees the runs of the rooms at
// their own location without any runtime or billing state, and files one
// report per run. A manager (booking_manage_external_runs) reviews it on the
// Runs page: accept voids the run, reject keeps it billable.

export type RunReportStatus = 'open' | 'accepted' | 'rejected';

export interface RunReport {
  id: string;
  status: RunReportStatus;
  message: string;
  photo_path: string | null;
  reported_at: string;
  reported_by_name: string | null;
  reviewed_at: string | null;
  // Only present in the manager feed.
  reviewed_by_name?: string | null;
  review_note: string | null;
}

export interface ReporterRoom {
  room_id: string;
  room_name: string;
  customer_name: string;
}

// What a reporter gets per run: when, where, which title, and their report.
// No duration, no completion, no billing status: that is the point.
export interface ReporterRun {
  id: string;
  started_at: string;
  pc_name: string | null;
  experience_name: string | null;
  report: RunReport | null;
}

export const REPORT_PHOTO_BUCKET = 'run-reports';
const SIGNED_URL_SECONDS = 600;

export async function listReportRooms(): Promise<ReporterRoom[]> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_report_list_rooms');
  if (error) throw error;
  return (data as ReporterRoom[]) ?? [];
}

export async function listReportRuns(
  roomId: string,
  fromUtc: string,
  toUtc: string,
): Promise<ReporterRun[]> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_report_list_runs', { p_room_id: roomId, p_from: fromUtc, p_to: toUtc });
  if (error) throw error;
  return ((data as { rows?: ReporterRun[] } | null)?.rows) ?? [];
}

export async function createRunReport(
  runId: string,
  message: string,
  photoPath: string | null,
): Promise<RunReportStatus> {
  const { data, error } = await supabase
    .schema('operations')
    .rpc('fn_report_create', { p_run_id: runId, p_message: message, p_photo_path: photoPath });
  if (error) throw error;
  return (data as { status: RunReportStatus }).status;
}

export async function reviewRunReport(
  reportId: string,
  decision: 'accepted' | 'rejected',
  note: string | null,
): Promise<void> {
  const { error } = await supabase
    .schema('operations')
    .rpc('fn_admin_review_run_report', {
      p_report_id: reportId,
      p_decision:  decision,
      p_note:      note,
    });
  if (error) throw error;
}

// Uploads under <room>/<run>/ — the only prefix the storage policy and
// fn_report_create accept. Returns the object path to store on the report.
export async function uploadReportPhoto(roomId: string, runId: string, file: File): Promise<string> {
  const { blob, contentType } = await downscaleImage(file);
  const ext = contentType === 'image/png' ? 'png' : contentType === 'image/webp' ? 'webp' : 'jpg';
  const path = `${roomId}/${runId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from(REPORT_PHOTO_BUCKET)
    .upload(path, blob, { contentType, upsert: false });
  if (error) throw error;
  return path;
}

// Private bucket: viewing needs a signed URL, which the storage read policy
// hands out to managers and to reporters of that room.
export async function getReportPhotoUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(REPORT_PHOTO_BUCKET)
    .createSignedUrl(path, SIGNED_URL_SECONDS);
  if (error) throw error;
  return data.signedUrl;
}
